import type { Route } from "~/api/types";
import { REFRESH_INTERVAL_MS } from "~/lib/constants";
import { getRouteScoreUsd } from "~/lib/routeMetrics";
import { useChains } from "~/hooks/useChains";
import { RouteCard } from "./RouteCard";
import { Skeleton } from "~/components/shared/Skeleton";

const REFRESH_TOTAL = REFRESH_INTERVAL_MS / 1000;
const RING_R = 7;
const RING_C = 2 * Math.PI * RING_R;

function RefreshButton({
  seconds,
  isRefreshing,
  onClick,
}: {
  seconds: number;
  isRefreshing: boolean;
  onClick: () => void;
}) {
  const progress = seconds / REFRESH_TOTAL;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-70"
      title="Refresh quotes"
    >
      <svg width="20" height="20" viewBox="0 0 20 20" className={isRefreshing ? "animate-spin" : ""}>
        {/* Background ring */}
        <circle
          cx="10" cy="10" r={RING_R}
          fill="none"
          stroke="var(--color-border-card-section)"
          strokeWidth="1.5"
        />
        {/* Countdown arc */}
        <circle
          cx="10" cy="10" r={RING_R}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeDasharray={RING_C}
          strokeDashoffset={RING_C * (1 - progress)}
          className="-rotate-90 origin-center"
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
        {/* Refresh arrow */}
        <path
          d="M12.5 7.5a3 3 0 1 1-1.2-1.2"
          fill="none"
          stroke="var(--color-text-tertiary)"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.5 5.5v2h-2"
          fill="none"
          stroke="var(--color-text-tertiary)"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function AboutContent() {
  const { chains, adapters } = useChains();

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      {/* Platforms */}
      <div className="flex flex-wrap justify-center gap-2">
        {adapters.map(({ displayName, logoUrl }) => (
          <div
            key={displayName}
            className="flex items-center gap-1.5 rounded-full border border-[var(--color-border-card-section)] px-3 py-1.5"
          >
            {logoUrl && <img src={logoUrl} alt={displayName} className="h-4 w-4 rounded-sm" />}
            <span className="text-xs font-medium text-[var(--color-text-tertiary)]">{displayName}</span>
          </div>
        ))}
      </div>

      {/* Center block */}
      <div className="flex flex-col items-center">
        <h3 className="text-lg font-bold text-[var(--color-text-primary)]">
          The Aggregator of Aggregators
        </h3>

        <p className="mt-3 text-center text-sm leading-relaxed text-[var(--color-text-secondary)]">
          CrossLlama finds the best route for your trade across multiple DEX
          aggregators and bridges, guaranteeing you the best execution prices in
          DeFi.
        </p>
      </div>

      {/* Chains */}
      <div className="flex flex-wrap justify-center gap-2">
        {chains.map(({ name, logoUri }) => (
          <div
            key={name}
            className="flex items-center gap-1 rounded-full bg-[var(--color-bg-card-section)] px-2 py-1"
          >
            <img src={logoUri} alt={name} className="h-3.5 w-3.5 rounded-full" />
            <span className="text-[11px] font-medium text-[var(--color-text-secondary)]">{name}</span>
          </div>
        ))}
      </div>

      <p className="text-xs text-[var(--color-text-tertiary)]">
        Enter an amount to compare routes.
      </p>
    </div>
  );
}

export function RouteList({
  routes,
  pending,
  isStreaming,
  isDone,
  selectedRouteId,
  onSelect,
  error,
  secondsUntilRefresh,
  onRefresh,
}: {
  routes: Route[];
  pending: string[];
  isStreaming: boolean;
  isDone: boolean;
  selectedRouteId: string | null;
  onSelect: (routeId: string) => void;
  error: Error | null;
  secondsUntilRefresh?: number | null;
  onRefresh?: () => void;
}) {
  const { adapters } = useChains();

  if (error) {
    return (
      <>
        <p className="text-sm text-[var(--color-danger)]">
          Failed to fetch routes
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
          {error.message}
        </p>
      </>
    );
  }

  if (!isStreaming && isDone && routes.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-tertiary)]">No routes found</p>
    );
  }

  if (!isStreaming && !isDone && routes.length === 0) {
    return <AboutContent />;
  }

  let bestAmount = 0n;
  if (routes.length > 0) {
    try { bestAmount = BigInt(routes[0].summary.toAmountEstimated); } catch { /* malformed */ }
  }
  const bestRouteUsd = routes.length > 0 ? getRouteScoreUsd(routes[0].summary) : null;

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[16px] font-semibold text-[var(--color-text-primary)]">
          Routes
        </p>
        {(secondsUntilRefresh != null || (isStreaming && routes.length > 0)) && onRefresh && (
          <RefreshButton
            seconds={secondsUntilRefresh ?? 0}
            isRefreshing={isStreaming && routes.length > 0}
            onClick={onRefresh}
          />
        )}
      </div>

      <div>
        {routes.map((route, i) => (
          <RouteCard
            key={route.routeId}
            route={route}
            isSelected={selectedRouteId ? route.routeId === selectedRouteId : i === 0}
            isFirst={i === 0}
            bestAmount={bestAmount}
            bestRouteUsd={bestRouteUsd}
            onClick={() => onSelect(route.summary.provider.name)}
          />
        ))}
      </div>

      {isStreaming && pending.length > 0 &&
        pending.map((name) => {
          const adapter = adapters.find((a) => a.name === name);
          return (
            <div
              key={name}
              className="mt-3 rounded-[var(--radius-lg)] border border-[var(--color-border-card-section)] bg-[var(--color-bg-card-section)] p-3"
            >
              {/* Row 1: shimmer amount + time */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="h-4 w-10" />
                </div>
                <Skeleton className="h-5 w-5 rounded-full" />
              </div>
              {/* Row 2: real adapter identity + shimmer gas */}
              <div className="mt-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <span className="text-[13px] text-[var(--color-text-tertiary)]">via</span>
                  {adapter?.logoUrl && (
                    <img src={adapter.logoUrl} alt="" className="h-4 w-4 rounded-sm" />
                  )}
                  <span className="text-[13px] font-medium text-[var(--color-text-secondary)]">
                    {adapter?.displayName ?? name}
                  </span>
                </div>
                <Skeleton className="h-3.5 w-14" />
              </div>
            </div>
          );
        })}
    </>
  );
}
